import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to manage video slideshow behavior
 *
 * Ensures:
 * - Video playback state controls slide advance
 * - Slide advances only when video naturally ends
 * - Single video slides pause after one play (no loop)
 * - Video position persists when navigating back
 * - Play/pause controls coordinate with video playback
 */
export const useVideoCarousel = ({
	visibleSlideIndex,
	autoplaySlides,
	autoplaySpeed,
	onAdvanceSlide,
	isSingleVideoSlide = false,
	currentSlideHasVideo = false,
	isCurrentSlideVisible = true,
	sliderRef = null,
	onSingleVideoEnd = () => {}
}) => {
	const videoPositionsRef = useRef({});
	const previousSlideIndexRef = useRef(visibleSlideIndex);
	const hasAdvancedRef = useRef(false);
	const slideTimerRef = useRef(null);
	const advanceTimerRef = useRef(null);
	const durationMetadataHandlerRef = useRef(null);
	const endedHandlerRef = useRef(null);
	const timeUpdateHandlerRef = useRef(null);
	const pauseAtEndHandlerRef = useRef(null);
	const boundVideoRef = useRef(null);
	const singleSlideEndHandlerRef = useRef(null);
	const singleSlideVideoRef = useRef(null);
	const singleSlideOriginalLoopRef = useRef(null);

	/**
	 * Get video element from slide DOM
	 */
	const getVideoFromSlide = useCallback(
		(slideIndex) => {
			const listElement = sliderRef?.current?.innerSlider?.list;
			if (listElement) {
				if (slideIndex === visibleSlideIndex) {
					// For current slide, prefer the active slick node to avoid binding
					// events on a non-active clone with the same data-index.
					const activeSlides = listElement.querySelectorAll(
						'.slick-slide.slick-active'
					);
					for (let i = 0; i < activeSlides.length; i += 1) {
						const video = activeSlides[i].querySelector(
							'video[data-is-video="true"]'
						);
						if (video) return video;
					}
				}

				const directSlide = listElement.querySelector(
					`.slick-slide[data-index="${slideIndex}"]`
				);
				if (directSlide) {
					const directVideo = directSlide.querySelector(
						'video[data-is-video="true"]'
					);
					if (directVideo) return directVideo;
				}
			}

			if (typeof document === 'undefined') return null;
			const sliderElement = document.querySelector(
				'.carousel.hide-buttons'
			);
			if (!sliderElement) return null;
			const slide = sliderElement.querySelector(
				`.slick-slide[data-index="${slideIndex}"] video[data-is-video="true"]`
			);
			return slide || null;
		},
		[sliderRef, visibleSlideIndex]
	);

	const saveVideoPosition = useCallback(
		(slideIndex) => {
			const video = getVideoFromSlide(slideIndex);
			if (video) {
				const { duration, currentTime } = video;
				const hasValidDuration =
					duration &&
					!Number.isNaN(duration) &&
					Number.isFinite(duration);
				const isCompleted =
					video.ended ||
					(hasValidDuration && currentTime >= duration - 0.15);

				// Completed videos should replay from start when revisited in loop.
				videoPositionsRef.current[slideIndex] = isCompleted
					? 0
					: currentTime;
			}
		},
		[getVideoFromSlide]
	);

	const restoreVideoPosition = useCallback(
		(slideIndex) => {
			const video = getVideoFromSlide(slideIndex);
			if (video && videoPositionsRef.current[slideIndex] !== undefined) {
				video.currentTime = videoPositionsRef.current[slideIndex];
			}
		},
		[getVideoFromSlide]
	);

	/**
	 * Handle slide transition: pause current video, save position, restore next video
	 */
	useEffect(() => {
		hasAdvancedRef.current = false;

		const previousSlideIndex = previousSlideIndexRef.current;
		const previousVideo = getVideoFromSlide(previousSlideIndex);
		if (previousVideo && previousSlideIndex !== visibleSlideIndex) {
			previousVideo.pause();
		}
		saveVideoPosition(previousSlideIndex);
		restoreVideoPosition(visibleSlideIndex);
		previousSlideIndexRef.current = visibleSlideIndex;
	}, [
		visibleSlideIndex,
		getVideoFromSlide,
		saveVideoPosition,
		restoreVideoPosition
	]);

	/**
	 * Initialize video and set up advance on ended event
	 * Video playback drives slide progression - no timer interference
	 */
	useEffect(() => {
		const video = getVideoFromSlide(visibleSlideIndex);

		// Always clear any pending polling timer from previous effect runs
		// This prevents stale timers from interfering with new slide setup
		if (slideTimerRef.current) {
			clearTimeout(slideTimerRef.current);
			slideTimerRef.current = null;
		}

		// Track the current effect instance to detect stale closures
		const effectSlideIndex = visibleSlideIndex;

		if (singleSlideVideoRef.current && singleSlideEndHandlerRef.current) {
			singleSlideVideoRef.current.removeEventListener(
				'ended',
				singleSlideEndHandlerRef.current
			);
			if (singleSlideOriginalLoopRef.current !== null) {
				singleSlideVideoRef.current.loop =
					singleSlideOriginalLoopRef.current;
			}
			singleSlideVideoRef.current = null;
			singleSlideEndHandlerRef.current = null;
			singleSlideOriginalLoopRef.current = null;
		}

		if (boundVideoRef.current && endedHandlerRef.current) {
			boundVideoRef.current.removeEventListener(
				'ended',
				endedHandlerRef.current
			);
			endedHandlerRef.current = null;
		}
		if (boundVideoRef.current && timeUpdateHandlerRef.current) {
			boundVideoRef.current.removeEventListener(
				'timeupdate',
				timeUpdateHandlerRef.current
			);
			timeUpdateHandlerRef.current = null;
		}
		if (boundVideoRef.current && pauseAtEndHandlerRef.current) {
			boundVideoRef.current.removeEventListener(
				'pause',
				pauseAtEndHandlerRef.current
			);
			pauseAtEndHandlerRef.current = null;
		}
		// Clear any pending advance timer (short-video loop-to-fill-timer path)
		if (advanceTimerRef.current) {
			clearTimeout(advanceTimerRef.current);
			advanceTimerRef.current = null;
		}
		// Remove any pending loadedmetadata listener used to detect short videos
		if (boundVideoRef.current && durationMetadataHandlerRef.current) {
			boundVideoRef.current.removeEventListener(
				'loadedmetadata',
				durationMetadataHandlerRef.current
			);
			durationMetadataHandlerRef.current = null;
		}
		boundVideoRef.current = null;

		// Helper to attach event handlers to a video element.
		// Detects whether the video is shorter than the autoplay timer.
		// - Short video: loops continuously and advances when the timer fires.
		// - Long video (or unknown duration): advances when the video naturally ends.
		const attachHandlers = (videoElement) => {
			// eslint-disable-next-line no-param-reassign
			videoElement.loop = false;
			hasAdvancedRef.current = false;

			// Reset video to beginning if it ended or is near the end.
			// tryAdvance fires from timeupdate at currentTime >= duration-0.15, so
			// the video can be paused near the end without ended=true. Without this
			// reset the first play-click would stop almost immediately.
			{
				const { duration, currentTime } = videoElement;
				const hasValidDur =
					duration &&
					!Number.isNaN(duration) &&
					Number.isFinite(duration);
				if (
					videoElement.ended ||
					(hasValidDur && currentTime >= duration - 0.25)
				) {
					// eslint-disable-next-line no-param-reassign
					videoElement.currentTime = 0;
				}
			}

			const tryAdvance = () => {
				if (!hasAdvancedRef.current && autoplaySlides) {
					hasAdvancedRef.current = true;
					onAdvanceSlide();
					// Single-slide: no next slide, but sync button to paused state
					if (isSingleVideoSlide) {
						onSingleVideoEnd();
					}
				}
			};

			// Applies the correct advance strategy once the video duration is known.
			const applyAdvanceStrategy = (duration) => {
				const hasValidDuration =
					duration &&
					!Number.isNaN(duration) &&
					Number.isFinite(duration);

				if (
					hasValidDuration &&
					duration < autoplaySpeed / 1000 &&
					!isSingleVideoSlide
				) {
					// Short multi-slide video: loop it and advance after the full autoplay timer
					// eslint-disable-next-line no-param-reassign
					videoElement.loop = true;
					advanceTimerRef.current = setTimeout(
						tryAdvance,
						autoplaySpeed
					);
					return;
				}

				// Long video (or unknown duration): event-driven advance on video end
				const onEnded = () => tryAdvance();
				const onTimeUpdate = () => {
					const { duration: d } = videoElement;
					if (
						d &&
						!Number.isNaN(d) &&
						Number.isFinite(d) &&
						videoElement.currentTime >= d - 0.15
					) {
						tryAdvance();
					}
				};
				const onPauseAtEnd = () => {
					if (videoElement.ended) {
						tryAdvance();
					}
				};

				videoElement.addEventListener('ended', onEnded);
				videoElement.addEventListener('timeupdate', onTimeUpdate);
				videoElement.addEventListener('pause', onPauseAtEnd);
				endedHandlerRef.current = onEnded;
				timeUpdateHandlerRef.current = onTimeUpdate;
				pauseAtEndHandlerRef.current = onPauseAtEnd;
			};

			boundVideoRef.current = videoElement;

			// readyState >= 1 (HAVE_METADATA) means duration is already available
			if (videoElement.readyState >= 1) {
				applyAdvanceStrategy(videoElement.duration);
			} else {
				const onMetadata = () => {
					durationMetadataHandlerRef.current = null;
					// Abort if the slide changed while waiting for metadata
					if (previousSlideIndexRef.current !== effectSlideIndex) {
						return;
					}
					applyAdvanceStrategy(videoElement.duration);
				};
				durationMetadataHandlerRef.current = onMetadata;
				videoElement.addEventListener('loadedmetadata', onMetadata, {
					once: true
				});
			}
		};

		if (!video) {
			if (!isCurrentSlideVisible || !autoplaySlides) {
				return;
			}

			// Video element not mounted yet. Poll for it so we can attach handlers
			// as soon as it appears (important for video-to-video transitions).
			let mountCheckCount = 0;
			const maxMountChecks = 50; // ~5 seconds at 100ms interval
			const checkForMount = () => {
				// Abort if this polling timer is stale (slide changed since effect ran)
				if (previousSlideIndexRef.current !== effectSlideIndex) {
					return;
				}

				mountCheckCount += 1;
				const mountedVideo = getVideoFromSlide(effectSlideIndex);
				if (mountedVideo) {
					// Video mounted - attach handlers now
					// Double-check we're still on the right slide and handlers aren't already attached
					if (
						previousSlideIndexRef.current === effectSlideIndex &&
						!boundVideoRef.current
					) {
						attachHandlers(mountedVideo);
					}
				} else if (mountCheckCount >= maxMountChecks) {
					// Video never mounted - advance to next slide
					if (previousSlideIndexRef.current === effectSlideIndex) {
						onAdvanceSlide();
					}
				} else {
					// Keep polling
					slideTimerRef.current = setTimeout(checkForMount, 100);
				}
			};
			slideTimerRef.current = setTimeout(checkForMount, 100);
			return;
		}

		hasAdvancedRef.current = false;

		// Use the shared helper to attach handlers
		attachHandlers(video);
	}, [
		autoplaySlides,
		autoplaySpeed,
		currentSlideHasVideo,
		isCurrentSlideVisible,
		visibleSlideIndex,
		getVideoFromSlide,
		isSingleVideoSlide,
		onAdvanceSlide,
		onSingleVideoEnd
	]);

	useEffect(() => {
		return () => {
			if (slideTimerRef.current) {
				clearTimeout(slideTimerRef.current);
			}
			if (advanceTimerRef.current) {
				clearTimeout(advanceTimerRef.current);
			}

			if (boundVideoRef.current && endedHandlerRef.current) {
				boundVideoRef.current.removeEventListener(
					'ended',
					endedHandlerRef.current
				);
			}
			if (boundVideoRef.current && timeUpdateHandlerRef.current) {
				boundVideoRef.current.removeEventListener(
					'timeupdate',
					timeUpdateHandlerRef.current
				);
			}
			if (boundVideoRef.current && pauseAtEndHandlerRef.current) {
				boundVideoRef.current.removeEventListener(
					'pause',
					pauseAtEndHandlerRef.current
				);
			}
			boundVideoRef.current = null;
			endedHandlerRef.current = null;
			timeUpdateHandlerRef.current = null;
			pauseAtEndHandlerRef.current = null;

			if (
				singleSlideVideoRef.current &&
				singleSlideEndHandlerRef.current
			) {
				singleSlideVideoRef.current.removeEventListener(
					'ended',
					singleSlideEndHandlerRef.current
				);
				if (singleSlideOriginalLoopRef.current !== null) {
					singleSlideVideoRef.current.loop =
						singleSlideOriginalLoopRef.current;
				}
			}
		};
	}, []);

	/**
	 * Handle play/pause state changes
	 * Only call play/pause when autoplaySlides state actually changes
	 */
	useEffect(() => {
		let retryTimer = null;
		let metadataHandler = null;
		let canPlayHandler = null;
		let targetVideo = null;

		const syncPlaybackState = () => {
			targetVideo = getVideoFromSlide(visibleSlideIndex);

			if (!targetVideo) {
				if (
					autoplaySlides &&
					currentSlideHasVideo &&
					isCurrentSlideVisible
				) {
					// Video node can mount slightly later than state changes; retry shortly.
					retryTimer = setTimeout(syncPlaybackState, 120);
				}
				return;
			}

			if (!autoplaySlides) {
				targetVideo.pause();
				return;
			}

			const playCurrentVideo = () => {
				const { duration, currentTime } = targetVideo;
				const hasValidDur =
					duration &&
					!Number.isNaN(duration) &&
					Number.isFinite(duration);
				if (
					targetVideo.ended ||
					(hasValidDur && currentTime >= duration - 0.25)
				) {
					targetVideo.currentTime = 0;
				}

				targetVideo.play().catch(() => {
					// Autoplay might be blocked by browser - silently fail.
				});
			};

			if (targetVideo.readyState >= 2) {
				playCurrentVideo();
				return;
			}

			metadataHandler = () => playCurrentVideo();
			canPlayHandler = () => playCurrentVideo();
			targetVideo.addEventListener('loadedmetadata', metadataHandler);
			targetVideo.addEventListener('canplay', canPlayHandler);
		};

		syncPlaybackState();

		return () => {
			if (retryTimer) {
				clearTimeout(retryTimer);
			}

			if (targetVideo && metadataHandler) {
				targetVideo.removeEventListener(
					'loadedmetadata',
					metadataHandler
				);
			}

			if (targetVideo && canPlayHandler) {
				targetVideo.removeEventListener('canplay', canPlayHandler);
			}
		};
	}, [
		autoplaySlides,
		currentSlideHasVideo,
		isCurrentSlideVisible,
		visibleSlideIndex,
		getVideoFromSlide
	]);

	useEffect(() => {
		const video = getVideoFromSlide(visibleSlideIndex);
		if (!video) {
			return () => {};
		}

		const handleUnexpectedPause = () => {
			const currentVideo = getVideoFromSlide(visibleSlideIndex);

			// Keep playback alive when state says play and current slide video pauses unexpectedly.
			if (autoplaySlides && currentVideo === video && !video.ended) {
				video.play().catch(() => {
					// Ignore browser/autoplay restrictions.
				});
			}
		};

		video.addEventListener('pause', handleUnexpectedPause);

		const cleanup = () => {
			video.removeEventListener('pause', handleUnexpectedPause);
		};

		return cleanup;
	}, [autoplaySlides, visibleSlideIndex, getVideoFromSlide]);

	useEffect(() => {
		if (
			!autoplaySlides ||
			!currentSlideHasVideo ||
			!isCurrentSlideVisible
		) {
			return () => {};
		}

		const watchdog = setInterval(() => {
			const video = getVideoFromSlide(visibleSlideIndex);
			if (!video) return;

			// Only treat as truly ended if ended flag is true AND the video
			// is actually at the end position. This prevents false positives
			// when wrapping back to a slide whose video has a stale ended flag
			// but has been reset to play from the beginning.
			if (video.ended) {
				const { duration, currentTime } = video;
				const atEndPosition =
					duration &&
					!Number.isNaN(duration) &&
					Number.isFinite(duration) &&
					currentTime >= duration - 0.5;

				if (
					atEndPosition &&
					!hasAdvancedRef.current &&
					autoplaySlides
				) {
					hasAdvancedRef.current = true;
					onAdvanceSlide();
					return;
				}

				// Video has stale ended flag but was reset to beginning - restart it
				if (!atEndPosition && video.readyState >= 2) {
					video.currentTime = 0;
					video.play().catch(() => {
						// Ignore browser/autoplay restrictions.
					});
				}
				return;
			}

			// Keep active slide video playing while state is play.
			if (video.paused && !video.ended && video.readyState >= 2) {
				video.play().catch(() => {
					// Ignore browser/autoplay restrictions.
				});
			}
		}, 250);

		return () => {
			clearInterval(watchdog);
		};
	}, [
		autoplaySlides,
		currentSlideHasVideo,
		isCurrentSlideVisible,
		visibleSlideIndex,
		getVideoFromSlide,
		onAdvanceSlide
	]);

	return {
		saveVideoPosition,
		restoreVideoPosition
	};
};
